<template>
    <div class="product-carousel-container mb-4">
        <div class="container-fluid px-0">
            <div class="row mb-3 align-items-center">
                <div class="col-7 col-md-6">
                    <div v-if="props.headline" class="carousel-headline">
                        <h2>{{ props.headline }}</h2>
                    </div>
                    <div v-if="props.tagline" class="carousel-tagline">
                        <p>{{ props.tagline }}</p>
                    </div>
                </div>
                <div class="col-5 col-md-6 text-end">
                    <div class="show-more-link" v-if="props.showMoreLinkText">
                        <Link :href="props.showMoreLinkAnchor" class="btn btn-outline-primary btn-sm">
                            {{ props.showMoreLinkText }}
                            <i class="bi bi-arrow-right ms-1"></i>
                        </Link>
                    </div>
                </div>
            </div>

            <div :id="props.componentIdentifier" class="carousel slide product-carousel" data-bs-ride="false">
                <!-- Carousel indicators -->
                <div class="carousel-indicators">
                    <button 
                        v-for="(chunk, index) in chunkedProducts" 
                        :key="index" 
                        type="button" 
                        :data-bs-target="'#' + props.componentIdentifier" 
                        :data-bs-slide-to="index" 
                        :class="{ active: index === 0 }" 
                        :aria-current="index === 0 ? 'true' : 'false'"
                        :aria-label="'Slide ' + (index + 1)">
                    </button>
                </div>

                <!-- Carousel items -->
                <div class="carousel-inner">
                    <div v-for="(chunk, index) in chunkedProducts" :key="index" :class="['carousel-item', { active: index === 0 }]">
                        <div class="row g-3">
                            <div v-for="product in chunk" :key="product.slug || index" :class="productColumnClass">
                                <div class="product-card">
                                    <Link :href="'/produkt/' + product.slug">
                                        <div class="product-image">
                                            <img v-if="product.images && product.images[0]" :src="`/images/${product.images[0].id}`" :alt="product.name">
                                            <img v-else src="/images/static/placeholder.png" alt="No current image">
                                        </div>
                                        <div class="product-info">
                                            <h5 class="product-title">{{ product.model_short_name }}</h5>
                                            <div class="product-price">
                                                <span class="text-muted old-price" v-if="product.oldPrice"><s>{{ product.oldPrice }}</s></span>
                                                <span class="current-price">{{ numberFormat(product.prices.lowest_price_including_shipping) }}</span>
                                                <span class="shipping-info">inkl. fragt</span>
                                            </div>
                                            <div class="product-meta">
                                                <div class="product-rating" v-if="product.rating">
                                                    <span class="rating-stars">
                                                        <i class="bi bi-star-fill" v-for="n in Math.floor(product.rating)" :key="n"></i>
                                                        <i class="bi bi-star-half" v-if="product.rating % 1 >= 0.5"></i>
                                                    </span>
                                                    <span class="rating-count" v-if="product.reviews">{{ product.reviews }}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Carousel controls -->
                <button class="carousel-control-prev" type="button" :data-bs-target="'#' + props.componentIdentifier" data-bs-slide="prev">
                    <span class="carousel-control-prev-icon" aria-hidden="true"></span>
                    <span class="visually-hidden">Previous</span>
                </button>
                <button class="carousel-control-next" type="button" :data-bs-target="'#' + props.componentIdentifier" data-bs-slide="next">
                    <span class="carousel-control-next-icon" aria-hidden="true"></span>
                    <span class="visually-hidden">Next</span>
                </button>
            </div>
        </div>
    </div>
</template>
  
<script setup>
    import { ref, onMounted, computed, nextTick } from 'vue';
    import { defineProps } from 'vue';
    import { Link } from '@inertiajs/vue3';

    // Try to get Bootstrap from window or import directly
    let bootstrapInstance;

    const props = defineProps({
        headline: {
            type: String,
            required: false,
            default: null
        },
        tagline: {
            type: String,
            required: false,
            default: ""
        },
        products: {
            type: Array,
            required: true,
        },
        productsPrPage: {
            type: Number,
            required: false,
            default: 4
        },
        showMoreLinkText: {
            type: String,
            required: false,
            default: ""
        },
        showMoreLinkAnchor: {
            type: String,
            required: false,
            default: ""
        },
        componentIdentifier: {
            type: String,
            required: true
        }
    });

    // Calculate product column class based on products per page
    const productColumnClass = computed(() => {
        return `col-6 col-md-${Math.floor(12 / Math.min(props.productsPrPage, 6))}`;
    });

    // Safely chunk products into groups for carousel slides
    const chunkedProducts = computed(() => {
        const result = [];
        
        // Ensure products is an array and has items
        if (!Array.isArray(props.products) || props.products.length === 0) {
            return [[]]; // Return empty chunk to avoid template errors
        }
        
        // Create a copy of the products array to avoid mutating props
        const productsArray = [...props.products];
        const chunkSize = parseInt(props.productsPrPage) || 4;
        
        // Split products into chunks of the specified size
        for (let i = 0; i < productsArray.length; i += chunkSize) {
            result.push(productsArray.slice(i, i + chunkSize));
        }
        
        return result.length > 0 ? result : [[]];
    });

    // Track carousel initialization status
    const carouselInitialized = ref(false);
    
    // Format number as currency
    function numberFormat(value) {
        value = Number(value);
        return new Intl.NumberFormat('da-DK', { 
            style: 'currency', 
            currency: 'DKK', 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
        }).format(value);
    }

    function goToProduct(productUrl) {
        window.location.href = productUrl;
    }

    // Initialize carousel with retries
    function initializeCarousel(retryCount = 0, maxRetries = 5) {
        // Try to get Bootstrap from global scope first
        bootstrapInstance = window.bootstrap;

        // Check if bootstrap is available
        if (bootstrapInstance && bootstrapInstance.Carousel) {
            try {
                const carouselElement = document.getElementById(props.componentIdentifier);
                if (carouselElement) {
                    // Add debug click handlers to arrows for troubleshooting
                    const prevButton = carouselElement.querySelector('.carousel-control-prev');
                    const nextButton = carouselElement.querySelector('.carousel-control-next');
                    
                    if (prevButton) {
                        prevButton.addEventListener('click', function(e) {
                            e.preventDefault(); // Prevent default to see if it's being triggered
                            const carouselObj = bootstrapInstance.Carousel.getInstance(carouselElement);
                            if (carouselObj) {
                                carouselObj.prev();
                            } else {
                                console.log('No carousel instance found');
                            }
                        });
                    }
                    
                    if (nextButton) {
                        nextButton.addEventListener('click', function(e) {
                            console.log('Next button clicked');
                            e.preventDefault(); // Prevent default to see if it's being triggered
                            const carouselObj = bootstrapInstance.Carousel.getInstance(carouselElement);
                            if (carouselObj) {
                                carouselObj.next();
                            } else {
                                console.log('No carousel instance found');
                            }
                        });
                    }
                    
                    // Setup indicator buttons click handlers
                    const indicatorButtons = carouselElement.querySelectorAll('.carousel-indicators button');
                    indicatorButtons.forEach((button, index) => {
                        button.addEventListener('click', function(e) {
                            e.preventDefault(); // Prevent default behavior
                            
                            const carouselObj = bootstrapInstance.Carousel.getInstance(carouselElement);
                            if (carouselObj) {
                                // Go to the slide at the specified index
                                carouselObj.to(index);
                            } else {
                                console.log('No carousel instance found when clicking indicator');
                            }
                        });
                    });
                    
                    // Destroy existing carousel if it exists
                    if (carouselElement._carousel) {
                        carouselElement._carousel.dispose();
                    }
                    
                    // Create new carousel instance with explicit options
                    const carousel = new bootstrapInstance.Carousel(carouselElement, {
                        interval: 5000,
                        wrap: true,
                        keyboard: true,
                        pause: 'hover',
                        touch: true // Enable touch swiping
                    });
                    
                    // Store carousel instance
                    carouselElement._carousel = carousel;
                    
                    // Add event listener for slide change (for debugging)
                    carouselElement.addEventListener('slide.bs.carousel', function(e) {
                        
                        // Update active indicator manually if needed
                        const indicators = carouselElement.querySelectorAll('.carousel-indicators button');
                        indicators.forEach((ind, i) => {
                            if (i === e.to) {
                                ind.classList.add('active');
                                ind.setAttribute('aria-current', 'true');
                            } else {
                                ind.classList.remove('active');
                                ind.setAttribute('aria-current', 'false');
                            }
                        });
                    });
                    
                    // Manually cycle
                    setTimeout(() => {
                        try {
                            carousel.cycle();
                        } catch (err) {
                            console.error('Error starting carousel cycle:', err);
                        }
                    }, 500);
                    
                    carouselInitialized.value = true;
                } else {
                    console.error(`Carousel element #${props.componentIdentifier} not found in DOM`);
                    if (retryCount < maxRetries) {
                        setTimeout(() => initializeCarousel(retryCount + 1, maxRetries), 300);
                    }
                }
            } catch (e) {
                console.error('Error initializing carousel:', e);
            }
        } else if (retryCount < maxRetries) {
            // Retry with increasing delay
            const delay = 300 * (retryCount + 1);
            console.log(`Bootstrap not available yet, retrying in ${delay}ms (attempt ${retryCount + 1}/${maxRetries})`);
            setTimeout(() => initializeCarousel(retryCount + 1, maxRetries), delay);
        } else {
            console.warn('Bootstrap not available after maximum retries');
            // Fallback: try to load Bootstrap directly if not found
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/bootstrap@5.2.3/dist/js/bootstrap.bundle.min.js';
            script.onload = () => initializeCarousel(0, 2);
            document.head.appendChild(script);
        }
    }

    onMounted(() => {
        // Give the DOM time to render before initializing
        nextTick(() => {
            // Start initialization process after DOM is ready
            initializeCarousel();
        });
        
        // Alternative: listen for bootstrap ready event if your app emits one
        document.addEventListener('bootstrap:loaded', () => {
            if (!carouselInitialized.value) {
                initializeCarousel();
            }
        });
        
        // Watch for Bootstrap appearance in the window object
        const bootstrapWatcher = setInterval(() => {
            if (window.bootstrap && !carouselInitialized.value) {
                clearInterval(bootstrapWatcher);
                initializeCarousel();
            }
        }, 500);
        
        // Clear watcher after reasonable time to prevent memory leaks
        setTimeout(() => clearInterval(bootstrapWatcher), 10000);
    });
</script>
  
<style scoped>
    .product-carousel-container {
        font-family: 'Montserrat', sans-serif;
    }

    .carousel-headline h2 {
        font-size: 1.1rem;
        font-weight: 700;
        text-transform: uppercase;
        color: #1E3A8A;
        margin-bottom: 0.5rem;
        padding-bottom: 0.5rem;
        border-bottom: 2px solid #f0f0f0;
    }

    .carousel-tagline {
        color: #666;
        font-size: 0.9rem;
        margin-bottom: 0;
    }

    .show-more-link a {
        color: #1E3A8A;
        font-weight: 500;
        font-size: 0.9rem;
        text-decoration: none;
    }

    .product-carousel {
        position: relative;
        border-radius: 0.5rem;
        overflow: hidden;
        padding-bottom: 30px;
    }

    /* Carousel indicators styling */
    .carousel-indicators {
        position: absolute;
        bottom: 0px;
        margin-bottom: 0;
        z-index: 10;
        display: flex;
        justify-content: center;
        padding: 0;
        margin-right: 15%;
        margin-left: 15%;
        visibility: visible !important;
        list-style: none;
    }

    .carousel-indicators button {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background-color: #007bff; /* Blue color for visibility */
        border: 1px solid #fff; /* White border for contrast */
        margin: 0 5px;
        opacity: 0.7;
        box-sizing: content-box;
        flex: 0 1 auto;
        cursor: pointer !important; /* Ensure cursor is a pointer */
        padding: 0;
        transition: all 0.3s ease;
    }

    .carousel-indicators button.active {
        background-color: #1E3A8A;
        opacity: 1;
        width: 12px;
        height: 12px;
        box-shadow: 0 0 5px rgba(255,255,255,0.5);
    }

    /* Carousel controls styling */
    .carousel-control-prev,
    .carousel-control-next {
        background: rgba(30, 58, 138, 0.6); /* Dark blue background */
        border-radius: 50%;
        width: 40px;
        height: 40px;
        top: 50%;
        transform: translateY(-50%);
        opacity: 1;
        transition: all 0.3s ease;
        z-index: 15;
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        cursor: pointer;
        border: 2px solid white; /* White border for visibility */
    }

    .carousel-control-prev {
        left: 10px;
    }

    .carousel-control-next {
        right: 10px;
    }

    .carousel-control-prev:hover,
    .carousel-control-next:hover {
        background: rgba(30, 58, 138, 0.9);
        transform: translateY(-50%) scale(1.1);
    }

    .carousel-control-prev-icon,
    .carousel-control-next-icon {
        width: 24px;
        height: 24px;
        filter: drop-shadow(0 0 2px rgba(0,0,0,0.5));
    }

    .product-carousel:hover .carousel-control-prev,
    .product-carousel:hover .carousel-control-next {
        opacity: 1;
    }

    /* Product card styling */
    .product-card {
        display: flex;
        flex-direction: column;
        height: 100%;
        background-color: #fff;
        border-radius: 0.5rem;
        overflow: hidden;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
        transition: transform 0.2s ease, box-shadow 0.2s ease;
        cursor: pointer;
    }

    .product-card:hover {
        transform: translateY(-3px);
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
    }

    .product-image {
        height: 140px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 1rem;
        background-color: #fff;
    }

    .product-image img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
    }

    .product-info {
        padding: 1rem;
        display: flex;
        flex-direction: column;
        flex-grow: 1;
    }

    .product-title {
        font-size: 0.9rem;
        font-weight: 500;
        margin-bottom: 0.5rem;
        line-height: 1.3;
        color: #333;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .product-price {
        margin-bottom: 0.5rem;
    }

    .old-price {
        display: block;
        font-size: 0.8rem;
        height: 1rem;
    }

    .current-price {
        font-size: 1rem;
        font-weight: 600;
        color: #1E3A8A;
    }

    .shipping-info {
        color: #888;
        font-size: 0.7rem;
        margin-left: 0.25rem;
    }

    .product-meta {
        margin-top: auto;
    }

    .product-rating {
        display: flex;
        align-items: center;
    }

    .rating-stars {
        color: #FFB800;
        font-size: 0.8rem;
        margin-right: 0.5rem;
    }

    .rating-count {
        color: #888;
        font-size: 0.75rem;
    }

    /* Responsive adjustments */
    @media (max-width: 767px) {
        .product-image {
            height: 120px;
        }
        
        .product-info {
            padding: 0.75rem;
        }
        
        .product-title {
            font-size: 0.85rem;
        }
        
        .current-price {
            font-size: 0.9rem;
        }
        
        .carousel-control-prev,
        .carousel-control-next {
            width: 30px;
            height: 30px;
        }
    }
</style>